import { Link, Route, Routes, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, download } from "../../api/client";
import type { Me, RoadDetail } from "../../api/types";
import { ConditionPill } from "../../components/Badges";
import { Icon } from "../../components/Icon";
import { LinkTabs, PageHeader } from "../../components/Layout";
import { EmptyState, ErrorState, Skeleton } from "../../components/States";
import { can } from "../../app/auth";
import { date, km, num } from "../../lib/format";
import { SummaryTab } from "./SummaryTab";
import { FindingsTab } from "./FindingsTab";
import { ImagesTab } from "./ImagesTab";
import { AssetsTab } from "./AssetsTab";
import { ReportTab } from "./ReportTab";
import { ApiError } from "../../api/client";

export function useRoad(id: string) {
  return useQuery({ queryKey: ["road", id], queryFn: () => api<RoadDetail>(`/api/roads/${id}`) });
}

export function RoadWorkspace({ me }: { me: Me }) {
  const { surveyId = "" } = useParams();
  const q = useRoad(surveyId);
  const base = `/roads/${surveyId}`;

  if (q.isPending) return <><Skeleton height={64} /><Skeleton height={40} /><Skeleton height={360} /></>;
  if (q.isError) {
    if (q.error instanceof ApiError && q.error.status === 404) {
      return <EmptyState title="This survey doesn't exist or isn't shared with you" action={<Link className="btn" to="/roads">Back to Roads</Link>} />;
    }
    return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  }
  const { survey: s, summary } = q.data;

  return (
    <>
      <PageHeader
        crumbs={[{ label: "Roads", to: "/roads" }, { label: `${s.road_code} ${s.road_name}` }]}
        title={<>{s.road_code} — {s.road_name}</>}
        subtitle={
          <span className="row" style={{ gap: "4px 12px" }}>
            <ConditionPill value={s.condition} provisional={s.condition_provisional} />
            <span>{[s.road_class && `Class ${s.road_class}`, s.agency, s.county, s.surface].filter(Boolean).join(" · ")}</span>
            <span className="num">{km(s.length_km, 2)}</span>
            <span>Surveyed {date(s.survey_date)}{s.surveyor ? ` by ${s.surveyor}` : ""}</span>
          </span>
        }
        actions={
          <>
            {can.review(me.role) && summary.unreviewed ? (
              <Link className="btn" to={`/review?survey=${s.survey_uuid}`}><Icon name="review" />Review {summary.unreviewed} finding{summary.unreviewed === 1 ? "" : "s"}</Link>
            ) : null}
            <button className="btn btn-primary" onClick={() => download(`/api/reports/road/${s.survey_uuid}?format=xlsx`)}><Icon name="download" />Download report</button>
          </>
        }
      />
      <LinkTabs label="Road sections" tabs={[
        { to: base, label: "Summary", end: true },
        { to: `${base}/findings`, label: "Findings", count: summary.verified + summary.unreviewed },
        { to: `${base}/images`, label: "Images", count: s.images },
        { to: `${base}/assets`, label: "Assets", count: q.data.asset_groups },
        { to: `${base}/report`, label: "Report" },
      ]} />
      <Routes>
        <Route index element={<SummaryTab road={q.data} />} />
        <Route path="findings" element={<FindingsTab road={q.data} me={me} />} />
        <Route path="images" element={<ImagesTab road={q.data} me={me} />} />
        <Route path="assets" element={<AssetsTab road={q.data} />} />
        <Route path="report" element={<ReportTab road={q.data} />} />
        <Route path="*" element={<EmptyState title="Unknown tab" action={<Link className="btn" to={base}>Go to Summary</Link>} />} />
      </Routes>
      <p className="muted" style={{ fontSize: 12 }}>Survey {s.survey_uuid} · {num(s.gps_points)} GPS points</p>
    </>
  );
}
